import { useEffect, useRef, useState } from "react";

/**
 * Pull-to-refresh gesture for iOS-style drag-down on any scroll container.
 * Usage: attach `bind` to the scroll container's props, `indicator` inside it at the top.
 *
 * Threshold: 60px pull past which release triggers onRefresh.
 */
export function usePullToRefresh(onRefresh) {
  const [pull, setPull] = useState(0);   // px pulled beyond top
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const targetRef = useRef(null);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (el.scrollTop <= 0) startY.current = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (startY.current == null || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && el.scrollTop <= 0) {
        setPull(Math.min(120, dy * 0.5));  // resistance
      } else {
        setPull(0);
      }
    };
    const onTouchEnd = async () => {
      if (pull >= 60 && !refreshing) {
        setRefreshing(true);
        try {
          if (navigator.vibrate) navigator.vibrate(10);
          await Promise.resolve(onRefresh?.());
        } finally {
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
      startY.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [onRefresh, pull, refreshing]);

  const bind = { ref: targetRef };
  const Indicator = () => (
    <div
      data-testid="ptr-indicator"
      className="flex items-center justify-center text-xs text-muted-foreground overflow-hidden transition-[height] duration-100"
      style={{ height: refreshing ? 40 : pull }}
    >
      {refreshing ? (
        <span className="animate-pulse">Refreshing…</span>
      ) : pull > 0 ? (
        <span>{pull >= 60 ? "Release to refresh" : "Pull to refresh"}</span>
      ) : null}
    </div>
  );

  return { bind, Indicator, refreshing };
}

/** Trigger a light vibration + return a class name to flash on the given side. */
export function haptic(side = "buy") {
  try {
    if (navigator.vibrate) navigator.vibrate(side === "buy" ? [10, 20, 20] : [15, 15, 30]);
  } catch {}
}
